import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, ArrowUpDown, Stethoscope, Briefcase, BadgeCheck, GraduationCap, Filter, X, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReviewCard from "@/components/ReviewCard";
import RatingForm from "@/components/RatingForm";
import { mockReviews, type Review, type ReviewType, getReviewTypeConfig } from "@/lib/mockData";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

interface BrowseReviewsProps {
  type: ReviewType;
}

type SortOption = "newest" | "highest" | "lowest" | "most-helpful" | "most-difficult" | "least-difficult";

const sortLabels: Record<SortOption, string> = {
  newest: "Most Recent",
  highest: "Highest Rated",
  lowest: "Lowest Rated",
  "most-helpful": "Most Helpful",
  "most-difficult": "Most Difficult",
  "least-difficult": "Least Difficult",
};

const BrowseReviews = ({ type }: BrowseReviewsProps) => {
  const [searchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [sort, setSort] = useState<SortOption>("newest");
  const [dbReviews, setDbReviews] = useState<Review[]>([]);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Filters
  const [preceptorFilter, setPreceptorFilter] = useState<string>("all");
  const [preceptorOpen, setPreceptorOpen] = useState(false);
  const [majorFilter, setMajorFilter] = useState<string>("all");
  const [majorOpen, setMajorOpen] = useState(false);
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [ratingFilter, setRatingFilter] = useState<string>("all");
  const [wouldDoAgainFilter, setWouldDoAgainFilter] = useState<string>("all");
  const [tagFilter, setTagFilter] = useState<string>("all");

  const hasActiveFilters =
    preceptorFilter !== "all" ||
    majorFilter !== "all" ||
    locationFilter !== "all" ||
    ratingFilter !== "all" ||
    wouldDoAgainFilter !== "all" ||
    tagFilter !== "all";

  const clearAllFilters = () => {
    setPreceptorFilter("all");
    setMajorFilter("all");
    setLocationFilter("all");
    setRatingFilter("all");
    setWouldDoAgainFilter("all");
    setTagFilter("all");
    setSearch("");
  };

  const handleWriteReview = () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "You must be signed in to write a review. Log in or create a free account to continue.",
        variant: "destructive",
        action: (
          <ToastAction altText="Go to login" onClick={() => navigate("/login")}>
            Sign in
          </ToastAction>
        ),
      });
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    setFormOpen(true);
  };

  const handleDeleteReview = async (id: string) => {
    if (!confirm("Are you sure you want to delete this review?")) return;
    const { error } = await supabase.from("reviews").delete().eq("id", id);
    if (error) {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Review deleted" });
    fetchReviews();
  };

  const fetchReviews = async () => {
    const { data } = await supabase
      .from("reviews_public" as any)
      .select("*")
      .eq("type", type)
      .order("created_at", { ascending: false }) as any;

    if (data) {
      const mapped: Review[] = data.map((r: any) => ({
        id: r.id,
        type: r.type,
        title: r.title,
        organization: r.organization,
        location: r.location,
        rating: Number(r.rating),
        difficulty: Number(r.difficulty),
        wouldDoAgain: r.would_do_again ?? true,
        review: r.review,
        author: r.author,
        date: r.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
        tags: r.tags || [],
        thumbsUp: r.thumbs_up || 0,
        thumbsDown: r.thumbs_down || 0,
        major: r.major || "",
        startDate: r.start_date ?? null,
        endDate: r.end_date ?? null,
        userId: null,
        isOwner: !!(r as any).is_owner,
        compensation: r.compensation ?? null,
        interviewInfo: r.interview_info ?? null,
        responsibilities: r.responsibilities ?? null,
        experienceKey: r.experience_key ?? null,
        isPioneer: !!r.is_pioneer,
      }));
      setDbReviews(mapped);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [type]);

  const allReviews = useMemo(() => dbReviews, [dbReviews]);

  const uniquePreceptors = useMemo(() => {
    const names = new Set<string>();
    allReviews.forEach((r) => r.preceptors?.forEach((p) => names.add(p.name)));
    return Array.from(names).sort();
  }, [allReviews]);

  const uniqueMajors = useMemo(() => {
    const majors = new Set<string>();
    const source = getReviewTypeConfig(type).categories;
    source.forEach((c) => majors.add(c));
    allReviews.forEach((r) => {
      const m = (r as any).major;
      if (m && m.trim()) majors.add(m.trim());
    });
    return Array.from(majors).sort();
  }, [allReviews, type]);

  const uniqueLocations = useMemo(() => {
    const locs = new Set<string>();
    allReviews.forEach((r) => { if (r.location) locs.add(r.location); });
    return Array.from(locs).sort();
  }, [allReviews]);

  const uniqueTags = useMemo(() => {
    const tags = new Set<string>();
    allReviews.forEach((r) => r.tags?.forEach((t) => tags.add(t)));
    return Array.from(tags).sort();
  }, [allReviews]);

  const filtered = useMemo(() => {
    let results = allReviews;

    if (preceptorFilter !== "all") {
      results = results.filter((r) => r.preceptors?.some((p) => p.name === preceptorFilter));
    }
    if (majorFilter !== "all") {
      const q = majorFilter.toLowerCase();
      results = results.filter((r) => {
        const m = ((r as any).major || "").toLowerCase();
        return m === q || m.includes(q);
      });
    }
    if (locationFilter !== "all") {
      results = results.filter((r) => r.location === locationFilter);
    }
    if (ratingFilter !== "all") {
      switch (ratingFilter) {
        case "4+": results = results.filter((r) => r.rating >= 4); break;
        case "3+": results = results.filter((r) => r.rating >= 3); break;
        case "2-": results = results.filter((r) => r.rating < 3); break;
      }
    }
    if (wouldDoAgainFilter !== "all") {
      results = results.filter((r) => r.wouldDoAgain === (wouldDoAgainFilter === "yes"));
    }
    if (tagFilter !== "all") {
      results = results.filter((r) => r.tags?.includes(tagFilter));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      results = results.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.organization.toLowerCase().includes(q) ||
          r.location.toLowerCase().includes(q) ||
          ((r as any).major || "").toLowerCase().includes(q) ||
          (r.tags || []).some((t) => t.toLowerCase().includes(q)) ||
          (r.review || "").toLowerCase().includes(q) ||
          r.preceptors?.some((p) => p.name.toLowerCase().includes(q) || p.specialty.toLowerCase().includes(q))
      );
    }

    switch (sort) {
      case "highest": return [...results].sort((a, b) => b.rating - a.rating);
      case "lowest": return [...results].sort((a, b) => a.rating - b.rating);
      case "most-helpful": return [...results].sort((a, b) => b.thumbsUp - a.thumbsUp);
      case "most-difficult": return [...results].sort((a, b) => b.difficulty - a.difficulty);
      case "least-difficult": return [...results].sort((a, b) => a.difficulty - b.difficulty);
      case "newest":
      default: return [...results].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
  }, [allReviews, search, sort, preceptorFilter, majorFilter, locationFilter, ratingFilter, wouldDoAgainFilter, tagFilter]);

  const searchedOrgStats = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    const orgReviews = allReviews.filter((r) => r.organization.toLowerCase().includes(q));
    if (orgReviews.length === 0) return null;
    const orgCounts: Record<string, number> = {};
    orgReviews.forEach((r) => {
      orgCounts[r.organization] = (orgCounts[r.organization] || 0) + 1;
    });
    const topOrg = Object.entries(orgCounts).sort((a, b) => b[1] - a[1])[0][0];
    const reviews = allReviews.filter((r) => r.organization === topOrg);
    return {
      organization: topOrg,
      count: reviews.length,
      avgRating: reviews.reduce((s, r) => s + r.rating, 0) / reviews.length,
      avgDifficulty: reviews.reduce((s, r) => s + r.difficulty, 0) / reviews.length,
      wouldDoAgainPct: Math.round((reviews.filter((r) => r.wouldDoAgain).length / reviews.length) * 100),
    };
  }, [allReviews, search]);

  const label = getReviewTypeConfig(type).labelPlural;
  const Icon = type === "internship" ? Briefcase : type === "residency" ? BadgeCheck : type === "fellowship" ? GraduationCap : Stethoscope;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Page header with gradient */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-transparent" />
        <div className="container relative py-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-serif text-3xl text-foreground">{label}</h1>
                <p className="text-muted-foreground text-sm mt-0.5">
                  {filtered.length} review{filtered.length !== 1 && "s"} from students
                </p>
              </div>
            </div>
            <Button onClick={handleWriteReview} className="gap-2 rounded-full px-6 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
              <Plus className="w-4 h-4" />
              Write a Review
            </Button>
          </motion.div>
        </div>
      </div>

      <main className="container py-8">
        {/* Organization stats */}
        {searchedOrgStats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-5 rounded-xl border border-border bg-card shadow-card"
          >
            <h3 className="font-serif text-lg text-foreground mb-4">{searchedOrgStats.organization}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="rounded-lg bg-primary/5 p-3 text-center">
                <div className="text-2xl font-bold text-primary">{searchedOrgStats.avgRating.toFixed(1)}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Avg Rating</div>
              </div>
              <div className="rounded-lg bg-accent/5 p-3 text-center">
                <div className="text-2xl font-bold text-accent">{searchedOrgStats.avgDifficulty.toFixed(1)}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Avg Difficulty</div>
              </div>
              <div className="rounded-lg bg-primary/5 p-3 text-center">
                <div className="text-2xl font-bold text-primary">{searchedOrgStats.wouldDoAgainPct}%</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Would Do Again</div>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <div className="text-2xl font-bold text-foreground">{searchedOrgStats.count}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Reviews</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Search + Filters + Sort */}
        <div className="flex flex-col gap-3 mb-8">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder={`Search ${label.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-lg" />
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Filter className="w-4 h-4 text-muted-foreground" />

            {uniquePreceptors.length > 0 && (
              <Popover open={preceptorOpen} onOpenChange={setPreceptorOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={preceptorOpen} className="w-[200px] rounded-lg h-9 text-sm justify-between font-normal">
                    <span className="truncate">{preceptorFilter === "all" ? "All Preceptors" : preceptorFilter}</span>
                    <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search preceptors..." />
                    <CommandList>
                      <CommandEmpty>No preceptor found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="all preceptors" onSelect={() => { setPreceptorFilter("all"); setPreceptorOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${preceptorFilter === "all" ? "opacity-100" : "opacity-0"}`} />
                          All Preceptors
                        </CommandItem>
                        {uniquePreceptors.map((name) => (
                          <CommandItem key={name} value={name} onSelect={() => { setPreceptorFilter(name); setPreceptorOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${preceptorFilter === name ? "opacity-100" : "opacity-0"}`} />
                            {name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            {uniqueMajors.length > 0 && (
              <Popover open={majorOpen} onOpenChange={setMajorOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={majorOpen} className="w-[200px] rounded-lg h-9 text-sm justify-between font-normal">
                    <span className="truncate">{majorFilter === "all" ? "All Majors" : majorFilter}</span>
                    <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search majors..." />
                    <CommandList>
                      <CommandEmpty>No major found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="all majors" onSelect={() => { setMajorFilter("all"); setMajorOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${majorFilter === "all" ? "opacity-100" : "opacity-0"}`} />
                          All Majors
                        </CommandItem>
                        {uniqueMajors.map((m) => (
                          <CommandItem key={m} value={m} onSelect={() => { setMajorFilter(m); setMajorOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${majorFilter === m ? "opacity-100" : "opacity-0"}`} />
                            {m}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-[170px] rounded-lg h-9 text-sm"><SelectValue placeholder="Location" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {uniqueLocations.map((loc) => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={ratingFilter} onValueChange={setRatingFilter}>
              <SelectTrigger className="w-[140px] rounded-lg h-9 text-sm"><SelectValue placeholder="Rating" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Rating</SelectItem>
                <SelectItem value="4+">4.0+ ★</SelectItem>
                <SelectItem value="3+">3.0+ ★</SelectItem>
                <SelectItem value="2-">Below 3.0 ★</SelectItem>
              </SelectContent>
            </Select>

            <Select value={wouldDoAgainFilter} onValueChange={setWouldDoAgainFilter}>
              <SelectTrigger className="w-[160px] rounded-lg h-9 text-sm"><SelectValue placeholder="Would Do Again" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Would Do Again?</SelectItem>
                <SelectItem value="yes">👍 Yes</SelectItem>
                <SelectItem value="no">👎 No</SelectItem>
              </SelectContent>
            </Select>

            {uniqueTags.length > 0 && (
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="w-[170px] rounded-lg h-9 text-sm"><SelectValue placeholder="Tag" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {uniqueTags.map((tag) => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
              <SelectTrigger className="w-[170px] rounded-lg h-9 text-sm">
                <ArrowUpDown className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(sortLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="gap-1 text-xs text-muted-foreground hover:text-foreground rounded-full">
                <X className="w-3.5 h-3.5" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Search className="w-7 h-7 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">No reviews found. Be the first to write one!</p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((review, i) => (
              <ReviewCard key={review.id} review={review} index={i} isAdmin={isAdmin} onDelete={handleDeleteReview} />
            ))}
          </div>
        )}
      </main>

      <RatingForm type={type} open={formOpen} onClose={() => setFormOpen(false)} onSubmitted={fetchReviews} />
      <Footer />
    </div>
  );
};

export default BrowseReviews;
